import { memo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { alpha } from '@mui/material/styles';
import { useBuilderTranslation } from '@/i18n/hooks/useTypedTranslation';

export type OtherTextMode = 'short' | 'long';

interface OtherOptionToggleProps {
  allowOtherText: boolean;
  otherTextMode?: OtherTextMode;
  onAllowOtherChange: (value: boolean) => void;
  onModeChange?: (mode: OtherTextMode) => void;
  description?: string;
}

export const OtherOptionToggle = memo(function OtherOptionToggle({
  allowOtherText,
  otherTextMode = 'short',
  onAllowOtherChange,
  onModeChange,
  description,
}: OtherOptionToggleProps) {
  const { t } = useBuilderTranslation();
  const displayDescription = description || t('options.otherDescription');

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1,
        }}
      >
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {t('options.allowOther')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {displayDescription}
          </Typography>
        </Box>
        <Switch
          checked={allowOtherText}
          onChange={(e) => onAllowOtherChange(e.target.checked)}
        />
      </Box>

      {allowOtherText && onModeChange && (
        <Box
          sx={{
            pl: 2,
            borderLeft: 3,
            borderColor: alpha('#14B8A6', 0.3),
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
            {t('options.otherMode.label')}
          </Typography>
          <ToggleButtonGroup
            value={otherTextMode}
            exclusive
            onChange={(_, value) => {
              if (value) {
                onModeChange(value as OtherTextMode);
              }
            }}
            size="small"
            sx={{ mb: 0.5 }}
          >
            <ToggleButton value="short" sx={{ px: 2 }}>
              {t('options.otherMode.short')}
            </ToggleButton>
            <ToggleButton value="long" sx={{ px: 2 }}>
              {t('options.otherMode.long')}
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {otherTextMode === 'short'
              ? t('options.otherMode.shortHint')
              : t('options.otherMode.longHint')}
          </Typography>
        </Box>
      )}
    </>
  );
});

export default OtherOptionToggle;